import React, { useMemo } from "react";
import { motion } from "framer-motion";
import FloatingCandlesticks from "@/components/animation/FloatingCandlesticks";
import SignalFlowAnimation from "@/components/animation/SignalFlowAnimation";
import { useAnimationSettings } from "@/components/animation/AnimationSettings";
import { useAuth } from "@/contexts/AuthContext";
import { PALETTE, COMPLIANCE_FOOTER_LINE } from "@/components/animation/MotionShared";

/**
 * Animated ORACLE Market Pulse hero. Premium cinematic banner that greets
 * the authenticated user, broadcasts the live posture, shows the signal flow,
 * and renders a soft floating candle backdrop.
 *
 * Strictly additive — never rendered above existing fixed UI. Mobile-safe.
 */
export default function MarketPulseHero({ testid = "market-pulse-hero" } = {}) {
  const { prefs, enabled } = useAnimationSettings();
  const auth = useAuth();
  const displayName = useMemo(() => {
    const u = auth?.user;
    if (!u) return null;
    const n = (u.name || "").trim();
    if (n) return n.split(" ")[0];
    const e = (u.email || "").trim();
    return e ? e.split("@")[0] : null;
  }, [auth]);
  const greeting = displayName ? `Welcome back, ${displayName}.` : "Welcome to CareLogix™ ORACLE.";

  return (
    <section style={S.shell} data-testid={testid}>
      {prefs.floatingCandles && enabled && <FloatingCandlesticks testid={`${testid}-candles`} />}
      <div style={S.content}>
        <div style={S.row}>
          <span style={S.tagline}>ORACLE™ Market Pulse</span>
          <span style={S.live}>● LIVE</span>
        </div>
        <motion.h2
          style={S.greet}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          data-testid={`${testid}-greeting`}
        >
          {greeting}
        </motion.h2>
        <p style={S.lede}>
          Stocks · Crypto · Forex · Sports · Healthcare · Startups · Grants · Global Signals · LOGIX-2 Active.
        </p>
        <SignalFlowAnimation active={3} testid={`${testid}-signal-flow`} />
        <span style={S.compliance}>{COMPLIANCE_FOOTER_LINE}</span>
      </div>
    </section>
  );
}

const S = {
  shell: { position: "relative", overflow: "hidden", background: "linear-gradient(135deg, #040608 0%, #0e3a2c 55%, #040608 100%)", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, color: PALETTE.slate3, padding: 18, minHeight: 220, boxShadow: "0 10px 40px rgba(0,0,0,0.55)", boxSizing: "border-box" },
  content: { position: "relative", zIndex: 2, display: "flex", flexDirection: "column", gap: 8 },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 },
  tagline: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.22em" },
  live: { color: "#ef4444", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.20em", fontWeight: 800, padding: "3px 9px", border: "1px solid #ef4444", borderRadius: 6, background: "rgba(239,68,68,0.10)" },
  greet: { color: PALETTE.goldBright, fontFamily: "'Figtree', system-ui, sans-serif", margin: "4px 0 0 0", fontSize: 28, lineHeight: 1.15, letterSpacing: "0.01em" },
  lede: { color: PALETTE.slate3, fontFamily: "'Figtree', system-ui, sans-serif", margin: "2px 0 8px 0", fontSize: 13.5, lineHeight: 1.55 },
  compliance: { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.06em" },
};
